<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { api } from '@/api';
import { buildWebhookPreviewUrl, forwardRelayBadge } from '@/utils/webhook-url';
import type { TenantResponse, CreateTenantRequest, TenantTaxRateResponse, ZeroVatRegimeType } from '@klever-webhook/shared';

const configs = ref<TenantResponse[]>([]);
const loading = ref(true);
const error = ref('');
const showForm = ref(false);
const editingId = ref<string | null>(null);
const expandedIds = ref<Set<string>>(new Set());

// Tax rates state per tenant
const taxRatesByTenant = ref<Record<string, TenantTaxRateResponse[]>>({});
const taxRatesLoading = ref<Record<string, boolean>>({});
const taxRatesRefreshing = ref<Record<string, boolean>>({});
const savedTaxRateIds = ref<Set<string>>(new Set());
const savingTaxRateIds = ref<Set<string>>(new Set());

function toggleExpand(id: string) {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
    loadTaxRates(id);
  }
}

const form = ref<CreateTenantRequest>({
  name: '',
  stripeWebhookSecret: '',
  stripeApiKey: '',
  falcoAppSecret: '',
  falcoApiKey: '',
  falcoEnvironment: 'sandbox',
  sendViaPeppol: false,
  sendAccounting: false,
  approveInvoices: true,
  invoiceExclTaxes: false,
  allowDirectUnpaidFinals: false,
  debugLogs: false,
  logRetentionCount: 100,
  notificationEmail: '',
});

const formTitle = computed(() => editingId.value ? 'Modifier la configuration' : 'Ajouter une configuration');

// The server's WEBHOOK_BASE_URL, read once from /health. Stays undefined until
// loaded, or when an older backend does not send it: the preview then falls back
// to the build-time API base (pre-migration behaviour).
const serverWebhookBaseUrl = ref<string | undefined>(undefined);

const previewWebhookUrl = computed(() => {
  if (editingId.value) {
    const config = configs.value.find((c: TenantResponse) => c.id === editingId.value);
    return config?.webhookUrl || '';
  }
  return form.value.id
    ? buildWebhookPreviewUrl(serverWebhookBaseUrl.value, import.meta.env.VITE_API_BASE_URL, form.value.id)
    : '';
});

async function loadConfigs() {
  try {
    configs.value = await api.getConfigs();
  } catch (e) {
    error.value = 'Impossible de charger les configurations';
  } finally {
    loading.value = false;
  }
}

async function loadWebhookBaseUrl() {
  try {
    serverWebhookBaseUrl.value = (await api.getHealth()).webhookBaseUrl;
  } catch {
    // Keep the fallback preview; the tenant list does not depend on /health.
  }
}

function openForm(config?: TenantResponse) {
  if (config) {
    editingId.value = config.id;
    form.value = {
      name: config.name,
      stripeWebhookSecret: '', // Don't expose secrets
      stripeApiKey: '',
      falcoAppSecret: '',
      falcoApiKey: '',
      falcoEnvironment: config.falcoEnvironment,
      sendViaPeppol: config.sendViaPeppol,
      sendAccounting: config.sendAccounting,
      approveInvoices: config.approveInvoices,
      invoiceExclTaxes: config.invoiceExclTaxes,
      allowDirectUnpaidFinals: config.allowDirectUnpaidFinals,
      debugLogs: config.debugLogs,
      logRetentionCount: config.logRetentionCount,
      notificationEmail: config.notificationEmail || '',
    };
  } else {
    editingId.value = null;
    form.value = {
      id: crypto.randomUUID(),
      name: '',
      stripeWebhookSecret: '',
      stripeApiKey: '',
      falcoAppSecret: '',
      falcoApiKey: '',
      falcoEnvironment: 'sandbox',
      sendViaPeppol: false,
      sendAccounting: false,
      approveInvoices: true,
      invoiceExclTaxes: false,
      allowDirectUnpaidFinals: false,
      debugLogs: false,
      logRetentionCount: 100,
      notificationEmail: '',
    };
  }
  showForm.value = true;
}

function closeForm() {
  showForm.value = false;
  editingId.value = null;
}

async function saveConfig() {
  try {
    if (editingId.value) {
      await api.updateConfig(editingId.value, form.value);
    } else {
      await api.createConfig(form.value);
    }
    closeForm();
    await loadConfigs();
  } catch (e) {
    error.value = 'Impossible de sauvegarder la configuration';
  }
}

async function deleteConfig(id: string) {
  if (!confirm('Êtes-vous sûr de vouloir supprimer cette configuration ?')) return;
  
  try {
    await api.deleteConfig(id);
    await loadConfigs();
  } catch (e) {
    error.value = 'Impossible de supprimer la configuration';
  }
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

// Enable/disable tenant
const showReenablePopup = ref(false);
const reenableTenantId = ref<string | null>(null);
const reenableProcessing = ref(false);
const reenableResult = ref<{ total: number; succeeded: number; failed: number } | null>(null);

const showDisablePopup = ref(false);
const disableTenantId = ref<string | null>(null);
const disableTenantName = ref('');

async function toggleEnabled(config: TenantResponse) {
  if (config.enabled) {
    // Disabling — show confirmation popup
    disableTenantId.value = config.id;
    disableTenantName.value = config.name;
    showDisablePopup.value = true;
  } else {
    // Enabling — show popup to ask about skipped events
    reenableTenantId.value = config.id;
    reenableResult.value = null;
    showReenablePopup.value = true;
  }
}

async function reenableWithRetry() {
  if (!reenableTenantId.value) return;
  reenableProcessing.value = true;
  try {
    await api.updateConfig(reenableTenantId.value, { enabled: true });
    reenableResult.value = await api.retrySkippedLogs(reenableTenantId.value);
    await loadConfigs();
  } catch (e) {
    error.value = 'Erreur lors du traitement des webhooks en attente';
  } finally {
    reenableProcessing.value = false;
  }
}

async function reenableWithoutRetry() {
  if (!reenableTenantId.value) return;
  try {
    await api.updateConfig(reenableTenantId.value, { enabled: true });
    showReenablePopup.value = false;
    await loadConfigs();
  } catch (e) {
    error.value = 'Impossible de réactiver la configuration';
  }
}

function closeReenablePopup() {
  showReenablePopup.value = false;
  reenableTenantId.value = null;
  reenableResult.value = null;
}

async function confirmDisable() {
  if (!disableTenantId.value) return;
  try {
    await api.updateConfig(disableTenantId.value, { enabled: false });
    await loadConfigs();
  } catch (e) {
    error.value = 'Impossible de désactiver la configuration';
  } finally {
    showDisablePopup.value = false;
    disableTenantId.value = null;
    disableTenantName.value = '';
  }
}

function cancelDisable() {
  showDisablePopup.value = false;
  disableTenantId.value = null;
  disableTenantName.value = '';
}

// Per-tenant /falco-invoice API token (show-once)
const showTokenPopup = ref(false);
const tokenTenantName = ref('');
const generatedToken = ref('');
const tokenCopied = ref(false);
const tokenCopyFailed = ref(false);
const generatingTokenIds = ref<Set<string>>(new Set());

async function generateFalcoToken(config: TenantResponse) {
  if (config.hasFalcoApiToken && !confirm(`Un jeton existe déjà pour ${config.name}. En générer un nouveau révoquera l'ancien. Continuer ?`)) {
    return;
  }
  generatingTokenIds.value.add(config.id);
  try {
    const result = await api.generateFalcoToken(config.id);
    generatedToken.value = result.token;
    tokenTenantName.value = config.name;
    tokenCopied.value = false;
    tokenCopyFailed.value = false;
    showTokenPopup.value = true;
    await loadConfigs();
  } catch (e) {
    error.value = 'Impossible de générer le jeton API';
  } finally {
    generatingTokenIds.value.delete(config.id);
  }
}

async function copyGeneratedToken() {
  try {
    await navigator.clipboard.writeText(generatedToken.value);
    tokenCopied.value = true;
    tokenCopyFailed.value = false;
  } catch {
    tokenCopied.value = false;
    tokenCopyFailed.value = true;
  }
}

function closeTokenPopup() {
  showTokenPopup.value = false;
  generatedToken.value = '';
  tokenTenantName.value = '';
  tokenCopied.value = false;
  tokenCopyFailed.value = false;
}

onMounted(() => {
  loadConfigs();
  loadWebhookBaseUrl();
});

const regimeOptions: { value: ZeroVatRegimeType; label: string }[] = [
  { value: 'vat_reverse_charge', label: 'Autoliquidation (VAT Reverse Charge)' },
  { value: 'intra_community', label: 'Intracommunautaire' },
  { value: 'export', label: 'Export' },
  { value: 'zero_rated_goods', label: 'Taux zéro' },
  { value: 'taxes_not_applicable', label: 'Taxes non applicables' },
  { value: 'other_exoneration', label: 'Autre exonération' },
];

async function loadTaxRates(tenantId: string) {
  taxRatesLoading.value[tenantId] = true;
  try {
    taxRatesByTenant.value[tenantId] = await api.getTaxRates(tenantId);
  } catch {
    // silently fail, will show empty
  } finally {
    taxRatesLoading.value[tenantId] = false;
  }
}

async function refreshTaxRates(tenantId: string) {
  taxRatesRefreshing.value[tenantId] = true;
  try {
    const result = await api.refreshTaxRates(tenantId);
    taxRatesByTenant.value[tenantId] = result.taxRates.length > 0
      ? result.taxRates
      : await api.getTaxRates(tenantId);
  } catch (e) {
    error.value = 'Impossible de rafraîchir les taux de taxe depuis Stripe';
  } finally {
    taxRatesRefreshing.value[tenantId] = false;
  }
}

async function updateTaxRateRegime(tenantId: string, taxRateId: string, regime: ZeroVatRegimeType | null, legalText?: string) {
  savingTaxRateIds.value.add(taxRateId);
  try {
    await api.updateTaxRate(tenantId, taxRateId, { falcoRegimeType: regime, legalText });
    await loadTaxRates(tenantId);
    savingTaxRateIds.value.delete(taxRateId);
    savedTaxRateIds.value.add(taxRateId);
    setTimeout(() => { savedTaxRateIds.value.delete(taxRateId); }, 2000);
  } catch {
    savingTaxRateIds.value.delete(taxRateId);
    error.value = 'Impossible de mettre à jour le régime TVA';
  }
}
</script>

<template>
  <div class="max-w-[1400px] mx-auto py-6 sm:px-6 lg:px-8">
    <div class="px-4 py-6 sm:px-0">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-semibold text-gray-900">Configurations</h1>
        <button
          @click="openForm()"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Ajouter une configuration
        </button>
      </div>

      <div v-if="error" class="mb-4 text-red-600">{{ error }}</div>

      <!-- Configs Table -->
      <div class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div v-if="loading" class="p-4 text-gray-500">Chargement...</div>
        <div v-else-if="configs.length === 0" class="p-4 text-gray-500">Aucune configuration. Ajoutez-en une pour commencer.</div>
        <table v-else class="w-full table-fixed divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="w-8 px-3 py-3"></th>
              <th class="w-36 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
              <th class="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Env.</th>
              <th class="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peppol</th>
              <th class="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compta</th>
              <th class="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approb.</th>
              <th class="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
              <th class="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Debug</th>
              <th class="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actif</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL du webhook</th>
              <th class="w-48 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="config in configs" :key="config.id">
              <tr class="cursor-pointer hover:bg-gray-50" @click="toggleExpand(config.id)">
                <td class="px-3 py-4 text-gray-400">
                  <svg
                    class="h-4 w-4 transition-transform duration-200"
                    :class="{ 'rotate-90': expandedIds.has(config.id) }"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ config.name }}</td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="config.falcoEnvironment === 'production' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                  >
                    {{ config.falcoEnvironment }}
                  </span>
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="config.sendViaPeppol ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'"
                  >
                    {{ config.sendViaPeppol ? 'Activé' : 'Désactivé' }}
                  </span>
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="config.sendAccounting ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-800'"
                  >
                    {{ config.sendAccounting ? 'Activé' : 'Désactivé' }}
                  </span>
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="config.approveInvoices ? (config.allowDirectUnpaidFinals ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800') : 'bg-amber-100 text-amber-800'"
                    :title="config.approveInvoices && config.allowDirectUnpaidFinals ? 'Décomptes directs impayés acceptés : numérotation sans paiement corroborant' : undefined"
                  >
                    {{ config.approveInvoices ? (config.allowDirectUnpaidFinals ? 'Auto + directs' : 'Auto') : 'Manuel' }}
                  </span>
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="config.invoiceExclTaxes ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-800'"
                  >
                    {{ config.invoiceExclTaxes ? 'HTVA' : 'TVAC' }}
                  </span>
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="config.debugLogs ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'"
                  >
                    {{ config.debugLogs ? 'Activé' : 'Désactivé' }}
                  </span>
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500" @click.stop>
                  <button
                    @click="toggleEnabled(config)"
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer"
                    :class="config.enabled ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'"
                  >
                    {{ config.enabled ? 'Actif' : 'Inactif' }}
                  </button>
                </td>
                <td class="px-6 py-4 text-sm text-gray-500" @click.stop>
                  <div class="flex items-center gap-2 min-w-0">
                    <code class="text-xs bg-gray-100 px-2 py-1 rounded truncate min-w-0 flex-1">{{ config.webhookUrl }}</code>
                    <button
                      @click="copyToClipboard(config.webhookUrl)"
                      class="text-indigo-600 hover:text-indigo-900 flex-shrink-0"
                      title="Copier dans le presse-papiers"
                    >
                      📋
                    </button>
                  </div>
                  <span
                    class="mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap"
                    :class="forwardRelayBadge(config.forwardWebhooks).classes"
                    :title="forwardRelayBadge(config.forwardWebhooks).title"
                  >
                    {{ forwardRelayBadge(config.forwardWebhooks).label }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" @click.stop>
                  <button @click="openForm(config)" class="text-indigo-600 hover:text-indigo-900 mr-4">Modifier</button>
                  <button @click="deleteConfig(config.id)" class="text-red-600 hover:text-red-900">Supprimer</button>
                </td>
              </tr>
              <!-- Accordion detail row -->
              <tr v-if="expandedIds.has(config.id)">
                <td colspan="10" class="px-6 py-4 bg-gray-50">
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <span class="block text-xs font-medium text-gray-500 uppercase">Rétention des logs</span>
                      <span class="text-gray-900">{{ config.logRetentionCount }} logs</span>
                    </div>
                    <div>
                      <span class="block text-xs font-medium text-gray-500 uppercase">Clé API Stripe</span>
                      <span
                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                        :class="config.hasStripeApiKey ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                      >
                        {{ config.hasStripeApiKey ? 'Configurée' : 'Manquante' }}
                      </span>
                    </div>
                    <div>
                      <span class="block text-xs font-medium text-gray-500 uppercase">Jeton API /falco-invoice</span>
                      <div class="flex items-center gap-2 mt-0.5">
                        <span
                          class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                          :class="config.hasFalcoApiToken ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                        >
                          {{ config.hasFalcoApiToken ? 'Configuré' : 'Non généré' }}
                        </span>
                        <button
                          @click.stop="generateFalcoToken(config)"
                          :disabled="generatingTokenIds.has(config.id)"
                          class="text-xs text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                        >
                          {{ generatingTokenIds.has(config.id) ? 'Génération...' : (config.hasFalcoApiToken ? 'Régénérer' : 'Générer') }}
                        </button>
                      </div>
                    </div>
                    <div>
                      <span class="block text-xs font-medium text-gray-500 uppercase">Email notification</span>
                      <span class="text-gray-900">{{ config.notificationEmail || '—' }}</span>
                    </div>
                    <div>
                      <span class="block text-xs font-medium text-gray-500 uppercase">Créé le</span>
                      <span class="text-gray-900">{{ new Date(config.createdAt).toLocaleDateString('fr-FR') }}</span>
                    </div>
                    <div>
                      <span class="block text-xs font-medium text-gray-500 uppercase">Modifié le</span>
                      <span class="text-gray-900">{{ new Date(config.updatedAt).toLocaleDateString('fr-FR') }}</span>
                    </div>
                  </div>

                  <!-- Tax Rates Section -->
                  <div class="border-t border-gray-200 pt-4">
                    <div class="flex items-center justify-between mb-3">
                      <h4 class="text-sm font-medium text-gray-700 uppercase">Taux de taxe 0% — Régimes Falco</h4>
                      <button
                        @click.stop="refreshTaxRates(config.id)"
                        :disabled="taxRatesRefreshing[config.id]"
                        class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg class="h-3.5 w-3.5 mr-1.5" :class="{ 'animate-spin': taxRatesRefreshing[config.id] }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {{ taxRatesRefreshing[config.id] ? 'Chargement...' : 'Rafraîchir depuis Stripe' }}
                      </button>
                    </div>

                    <div v-if="taxRatesLoading[config.id]" class="text-sm text-gray-400">Chargement des taux de taxe...</div>
                    <div v-else-if="!taxRatesByTenant[config.id] || taxRatesByTenant[config.id].filter(t => t.percentage === 0).length === 0" class="text-sm text-gray-400">
                      Aucun taux de taxe 0%. Cliquez sur « Rafraîchir depuis Stripe » pour importer.
                    </div>
                    <table v-else class="w-full text-sm">
                      <thead>
                        <tr class="text-xs text-gray-500 uppercase">
                          <th class="text-left py-1 pr-3">Nom</th>
                          <th class="text-left py-1 pr-3">ID Stripe</th>
                          <th class="text-left py-1 pr-3">Taux</th>
                          <th class="text-left py-1 pr-3">Régime Falco</th>
                          <th class="text-left py-1 pr-3">Texte légal</th>
                          <th class="w-6"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="tr in taxRatesByTenant[config.id]?.filter(t => t.percentage === 0)" :key="tr.id" class="border-t border-gray-100">
                          <td class="py-2 pr-3 text-gray-900">{{ tr.displayName }}</td>
                          <td class="py-2 pr-3">
                            <code class="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{{ tr.stripeTaxRateId }}</code>
                          </td>
                          <td class="py-2 pr-3 text-gray-600">{{ tr.percentage }}%</td>
                          <td class="py-2 pr-3">
                            <select
                              :value="tr.falcoRegimeType || ''"
                              @change="updateTaxRateRegime(config.id, tr.id, ($event.target as HTMLSelectElement).value as ZeroVatRegimeType || null)"
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs border px-2 py-1"
                            >
                              <option value="">— Non configuré —</option>
                              <option v-for="opt in regimeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                            </select>
                            <span v-if="!tr.falcoRegimeType" class="text-xs text-red-500 mt-0.5 block">Configuration requise</span>
                          </td>
                          <td class="py-2">
                            <input
                              v-if="tr.falcoRegimeType === 'other_exoneration'"
                              :value="tr.legalText"
                              @blur="updateTaxRateRegime(config.id, tr.id, tr.falcoRegimeType, ($event.target as HTMLInputElement).value)"
                              placeholder="Ex: Autoliquidation - art. 39bis"
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs border px-2 py-1"
                            />
                            <span v-else class="text-gray-400 text-xs">—</span>
                          </td>
                          <td class="py-2 pl-2 text-center w-6">
                            <svg v-if="savingTaxRateIds.has(tr.id)" class="h-4 w-4 text-gray-400 inline-block animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <transition v-else name="fade">
                              <svg v-if="savedTaxRateIds.has(tr.id)" class="h-4 w-4 text-green-500 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </transition>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Disable Confirmation Popup -->
      <div v-if="showDisablePopup" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-medium text-gray-900">Désactiver la configuration</h3>
          </div>
          <div class="px-6 py-4 space-y-4">
            <p class="text-sm text-gray-700">
              Êtes-vous sûr de vouloir désactiver <span class="font-semibold">{{ disableTenantName }}</span> ?
              Les webhooks reçus pendant la désactivation ne seront pas traités.
            </p>
            <div class="flex justify-end space-x-3">
              <button
                @click="cancelDisable"
                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                @click="confirmDisable"
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Désactiver
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Re-enable Popup -->
      <div v-if="showReenablePopup" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-medium text-gray-900">Réactiver la configuration</h3>
          </div>
          <div class="px-6 py-4">
            <div v-if="reenableResult" class="space-y-3">
              <p class="text-sm text-gray-700">Traitement terminé :</p>
              <div class="grid grid-cols-3 gap-3 text-center">
                <div class="bg-gray-50 rounded p-2">
                  <div class="text-lg font-semibold text-gray-900">{{ reenableResult.total }}</div>
                  <div class="text-xs text-gray-500">Total</div>
                </div>
                <div class="bg-green-50 rounded p-2">
                  <div class="text-lg font-semibold text-green-700">{{ reenableResult.succeeded }}</div>
                  <div class="text-xs text-gray-500">Succès</div>
                </div>
                <div class="bg-red-50 rounded p-2">
                  <div class="text-lg font-semibold text-red-700">{{ reenableResult.failed }}</div>
                  <div class="text-xs text-gray-500">Échecs</div>
                </div>
              </div>
              <div class="flex justify-end pt-2">
                <button
                  @click="closeReenablePopup"
                  class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Fermer
                </button>
              </div>
            </div>
            <div v-else class="space-y-4">
              <p class="text-sm text-gray-700">
                Des webhooks ont pu être reçus pendant que cette configuration était désactivée.
                Souhaitez-vous les traiter maintenant ?
              </p>
              <div class="flex justify-end space-x-3">
                <button
                  @click="reenableWithoutRetry"
                  class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Ignorer
                </button>
                <button
                  @click="reenableWithRetry"
                  :disabled="reenableProcessing"
                  class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {{ reenableProcessing ? 'Traitement en cours...' : 'Traiter les webhooks' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Falco API Token Popup (show-once) -->
      <div v-if="showTokenPopup" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-medium text-gray-900">Jeton API généré — {{ tokenTenantName }}</h3>
          </div>
          <div class="px-6 py-4 space-y-4">
            <p class="text-sm text-red-600 font-medium">
              Ce jeton ne sera plus jamais affiché. Copiez-le maintenant.
            </p>
            <div class="flex items-center gap-2">
              <code class="flex-1 text-xs bg-gray-100 px-3 py-2 rounded border border-gray-300 break-all select-all">{{ generatedToken }}</code>
              <button
                type="button"
                @click="copyGeneratedToken"
                class="text-indigo-600 hover:text-indigo-900 flex-shrink-0"
                title="Copier dans le presse-papiers"
                aria-label="Copier le jeton dans le presse-papiers"
              >
                {{ tokenCopied ? '✅' : '📋' }}
              </button>
            </div>
            <p v-if="tokenCopyFailed" class="text-xs text-red-600">
              Copie impossible — sélectionnez le jeton manuellement.
            </p>
            <p class="text-xs text-gray-500">
              À utiliser comme <code>Authorization: Bearer &lt;jeton&gt;</code> sur <code>POST /falco-invoice/&lt;tenantId&gt;</code>.
            </p>
            <div class="flex justify-end">
              <button
                @click="closeTokenPopup"
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Modal -->
      <div v-if="showForm" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-[90vh]">
          <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 class="text-lg font-medium text-gray-900">{{ formTitle }}</h3>
          </div>
          <form @submit.prevent="saveConfig" class="px-6 py-4 space-y-4 overflow-y-auto flex-1">
            <div>
              <label class="block text-sm font-medium text-gray-700">Nom</label>
              <input
                v-model="form.name"
                type="text"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              />
            </div>
            <div v-if="previewWebhookUrl">
              <label class="block text-sm font-medium text-gray-700">URL du webhook Stripe</label>
              <div class="mt-1 flex items-center gap-2">
                <code class="flex-1 text-xs bg-gray-100 px-3 py-2 rounded border border-gray-300 break-all select-all">{{ previewWebhookUrl }}</code>
                <button
                  type="button"
                  @click="copyToClipboard(previewWebhookUrl)"
                  class="text-indigo-600 hover:text-indigo-900 flex-shrink-0"
                  title="Copier dans le presse-papiers"
                >
                  📋
                </button>
              </div>
              <p class="mt-1 text-xs text-gray-500">Utilisez cette URL pour configurer le webhook dans Stripe.</p>
              <p v-if="editingId" class="mt-1 text-xs text-amber-700">
                Jusqu'à la phase 2 de la migration K8s, cette URL est la cible : elle peut différer de celle que contient encore l'endpoint Stripe du tenant.
              </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Secret du webhook Stripe
                <span v-if="editingId" class="text-gray-400">(laisser vide pour conserver l'actuel)</span>
              </label>
              <input
                v-model="form.stripeWebhookSecret"
                type="password"
                :required="!editingId"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Clé API Stripe
                <span v-if="editingId" class="text-gray-400">(laisser vide pour conserver l'actuel)</span>
              </label>
              <input
                v-model="form.stripeApiKey"
                type="password"
                :required="!editingId"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Secret App Falco
                <span v-if="editingId" class="text-gray-400">(laisser vide pour conserver l'actuel)</span>
              </label>
              <input
                v-model="form.falcoAppSecret"
                type="password"
                :required="!editingId"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Clé API Falco
                <span v-if="editingId" class="text-gray-400">(laisser vide pour conserver l'actuel)</span>
              </label>
              <input
                v-model="form.falcoApiKey"
                type="password"
                :required="!editingId"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Environnement Falco</label>
              <select
                v-model="form.falcoEnvironment"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div class="flex items-center">
              <input
                id="sendViaPeppol"
                v-model="form.sendViaPeppol"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="sendViaPeppol" class="ml-2 block text-sm text-gray-700">
                Envoyer via Peppol (si le client a un n° VAT)
              </label>
            </div>
            <div class="flex items-center">
              <input
                id="sendAccounting"
                v-model="form.sendAccounting"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="sendAccounting" class="ml-2 block text-sm text-gray-700">
                Envoi compta
              </label>
            </div>
            <div class="flex items-center">
              <input
                id="approveInvoices"
                v-model="form.approveInvoices"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="approveInvoices" class="ml-2 block text-sm text-gray-700">
                Approuver (sinon brouillon Falco)
              </label>
            </div>
            <div class="flex items-center">
              <input
                id="invoiceExclTaxes"
                v-model="form.invoiceExclTaxes"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="invoiceExclTaxes" class="ml-2 block text-sm text-gray-700">
                Prix HTVA sur les factures (sinon TVAC)
              </label>
            </div>
            <div class="flex items-center">
              <input
                id="allowDirectUnpaidFinals"
                v-model="form.allowDirectUnpaidFinals"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="allowDirectUnpaidFinals" class="ml-2 block text-sm text-gray-700" title="Accepte les décomptes directs impayés (et notes de crédit directes) malgré l'approbation automatique : le document est numéroté sans paiement corroborant. Réservé aux intégrations de confiance (mydimm).">
                Décomptes directs impayés (numérotés sans paiement)
              </label>
            </div>
            <div class="flex items-center">
              <input
                id="debugLogs"
                v-model="form.debugLogs"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="debugLogs" class="ml-2 block text-sm text-gray-700">
                Debug logs (VAT + Falco)
              </label>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Nombre de logs conservés (10-1000)</label>
              <input
                v-model.number="form.logRetentionCount"
                type="number"
                min="10"
                max="1000"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Email de notification (optionnel)</label>
              <input
                v-model="form.notificationEmail"
                type="email"
                placeholder="alerts@example.com"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
              />
              <p class="mt-1 text-xs text-gray-500">Reçoit les alertes en cas d’erreur webhook (en plus du destinataire global).</p>
            </div>
            <div class="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                @click="closeForm"
                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active { transition: opacity 0.15s ease-in; }
.fade-leave-active { transition: opacity 0.6s ease-out; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }
</style>
